import React, { useState } from "react";
import { motion } from "framer-motion";
import ConnectModal from "../components/ConnectModal";

const fadeUp = {
    hidden: { opacity: 0, y: 10 },
    visible: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: {
            duration: 1.1,
            delay: i * 0.18,
            ease: [0.22, 1, 0.36, 1],
        },
    }),
};

const Line = ({ children, delay = 0, className = "" }) => (
    <motion.p
        variants={fadeUp}
        custom={delay}
        initial="hidden"
        animate="visible"
        className={`serif font-light leading-[1.35] ${className}`}
        style={{ color: "var(--sage-text)" }}
    >
        {children}
    </motion.p>
);

export default function Landing() {
    const [open, setOpen] = useState(false);

    return (
        <div className="App grain relative overflow-hidden">
            {/* Top bar — almost invisible */}
            <header className="relative z-10 px-6 sm:px-10 pt-8 sm:pt-10">
                <div className="flex items-center justify-between">
                    <span
                        className="eyebrow"
                        data-testid="brand-mark"
                        style={{ color: "var(--sage-text)" }}
                    >
                        Milda Petrus
                    </span>
                    <span
                        className="eyebrow hidden sm:block"
                        style={{ color: "var(--sage-text)" }}
                    >
                        a practice
                    </span>
                </div>
                <div className="hairline mt-6" />
            </header>

            {/* HERO — name + all statements + Connect on one fold */}
            <main
                className="relative z-10 px-6 sm:px-10"
                data-testid="hero-section"
            >
                <div className="max-w-[1180px] mx-auto grid grid-cols-12 pt-[16vh] sm:pt-[20vh]">
                    <div className="col-span-12 md:col-span-9 md:col-start-2 space-y-5 sm:space-y-6">
                        <Line
                            delay={0}
                            className="text-base sm:text-lg md:text-xl tracking-[0.01em]"
                        >
                            <span data-testid="hero-name">
                                Human. Endlessly fascinating.
                            </span>
                        </Line>

                        <Line
                            delay={1}
                            className="text-base sm:text-lg md:text-xl tracking-[0.01em]"
                        >
                            <span data-testid="body-mind-spirit">
                                Body
                                <span
                                    className="mx-2"
                                    style={{ opacity: 0.55 }}
                                >
                                    ·
                                </span>
                                Mind
                                <span
                                    className="mx-2"
                                    style={{ opacity: 0.55 }}
                                >
                                    ·
                                </span>
                                Spirit
                            </span>
                        </Line>

                        <Line
                            delay={2}
                            className="text-base sm:text-lg md:text-xl tracking-[0.01em]"
                        >
                            Science and Wisdom. Logic and Intuition.
                        </Line>

                        <Line
                            delay={3}
                            className="text-base sm:text-lg md:text-xl tracking-[0.01em]"
                        >
                            Honoring both the evidence and the experience.
                        </Line>

                        <Line
                            delay={4}
                            className="text-base sm:text-lg md:text-xl tracking-[0.01em]"
                        >
                            Through deep relaxation.
                        </Line>

                        <motion.div
                            variants={fadeUp}
                            custom={6}
                            initial="hidden"
                            animate="visible"
                            className="pt-24 sm:pt-32 md:pt-36"
                            data-testid="contact-section"
                        >
                            <button
                                onClick={() => setOpen(true)}
                                className="serif text-xs sm:text-sm tracking-[0.36em] uppercase underline-grow"
                                style={{
                                    color: "var(--sage-text)",
                                    fontWeight: 300,
                                }}
                                data-testid="connect-button"
                            >
                                Connect
                            </button>
                        </motion.div>
                    </div>
                </div>
            </main>

            {/* Warm wall + diagonal ray as ambient background */}
            <div
                className="absolute inset-0 pointer-events-none overflow-hidden"
                data-testid="photographic-moment"
                aria-hidden="true"
                style={{ zIndex: 0 }}
            >
                <img
                    src="https://customer-assets.emergentagent.com/job_milda-petrus/artifacts/gb4pvx87_hf_20260605_215339_dcee61c0-8f9d-49b5-bc8e-0769c63bec52.png"
                    alt=""
                    className="w-full h-full object-cover"
                    style={{
                        objectPosition: "center center",
                        filter: "saturate(0.95) brightness(1.04)",
                    }}
                />
                {/* Soft cream wash on the left so the text stays calm and readable */}
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(to right, rgba(247,243,236,0.55) 0%, rgba(247,243,236,0.3) 32%, rgba(247,243,236,0.08) 65%, rgba(247,243,236,0) 100%)",
                    }}
                />
            </div>

            <ConnectModal open={open} onClose={() => setOpen(false)} />
        </div>
    );
}
